import { defaultLanguage } from '../../../Intl/setup.js';

import { formatPrice, getPriceCent, getPriceFromCent } from '../../util/price.js';

import { getProductPrice } from '../Product/ProductActions.js';
import { getUserOption } from '../User/UserActions.js';
import { getTranslation } from '../Intl/IntlHelper.js';
import { getRandomString, slugify } from '../../util/text.js';

export const SET_ITEM = 'SET_ITEM';
export const SET_ITEM_QUANTITY = 'SET_ITEM_QUANTITY';
export const REMOVE_ITEM = 'REMOVE_ITEM';
export const REMOVE_ITEMS = 'REMOVE_ITEMS';
export const SET_INVOICE_ADDRESS = 'SET_INVOICE_ADDRESS';
export const SET_SHIPPING_ADDRESS = 'SET_SHIPPING_ADDRESS';
export const SET_SHIPPING_METHOD = 'SET_SHIPPING_METHOD';
export const SET_CART_OPTION = 'SET_CART_OPTION';
export const ADD_CART_NOTE = 'ADD_CART_NOTE';
export const CLEAR_CART = 'CLEAR_CART';

// Getters
export function getCart(store) {
    return store.cart;
}

export function getCartItems(store) {
    return store.cart.items;
}

export function getCartItemByProduct(store, product) {
    return store.cart.items.find(item => item.productId === product._id);
}

export function getCartNote(store) {
    return store.cart.note;
}

export function getCartShipping(store) {
    return store.cart.shipping;
}

export function getCartInvoice(store) {
    return store.cart.invoice;
}

export function getCartAddress(store, type) {
    return (store.cart[type] || {}).address;
}

export function getCartVouchers(store) {
    return store.cart.vouchers;
}

export function getCartOptions(store) {
    return store.cart.options || {};
}

export function getCartItemFromProduct(product, quantity = 1, locale = defaultLanguage, extra = {}) {
    if(product) {
        const { _id, __v, priceTTC, priceHT, weightNet, description, translations, pictures, annexes, isHighlight, isActive, ...productData } = product;
		// console.log('Cart::: getCartItemFromProduct:', product, quantity, getProductPrice(product, quantity, true, extra.referral), getProductPrice(product, quantity, true, extra.referral) / parseInt(quantity || 1, 10));
        return {
			...productData,
			id: getRandomString(4),
            productId: product._id || product.productId, // product or item
            quantity: parseInt(quantity, 10),
			priceTTC,
			priceHT,
            basePriceTTC: priceTTC,
            basePriceHT: priceHT,
            status: 'ordered',
            name: getTranslation(product, 'name', locale),
            nameAdmin: product.name,
            ...(extra || {}),
        };
    }
	return null;
}

export function getCartQuantityStep(product) {
    return Math.max(product.stepQuantity || 1, 1);
}

export function getPackages(items) {
    const packages = [];
    (items || []).forEach(item => {
        if(item.packing && item.quantity > item.packing) {
            const threshold = Math.ceil(item.quantity / item.packing);
            for(let i = 0; i < threshold; i += 1) {
                packages.push({
                    items: [{
                        ...item,
                        quantity: (i + 1) !== threshold ? item.packing : item.quantity - (i * item.packing),
                    }],
                });
            }
        } else {
            packages.push({ items: [{ ...item }] });
        }
    });
    // console.log('Order packages', packages);
    return packages;
}

export function getParcels(method, items) {
    if(!method.parcelGroup) {
        // console.log('getParcels ::: Method without parcel grouping');
        return [[{ items }]];
    }

    const parcels = [];
    const orderPackages = getPackages(items);

    for(let i = 0; i < Math.ceil(orderPackages.length / method.parcelGroup); i += 1) {
        parcels.push([...orderPackages.slice(i * method.parcelGroup, (i + 1) * method.parcelGroup)]);
    }

    // console.log('orderParcels', parcels);
    return parcels;
}

export function getTotalWeight(items, products = null) {
    return getPriceFromCent((items || []).filter(item => item.status === 'ordered').reduce((total, item) => {
        const product = false && products && products.length ? products.find(product => product._id === item.productId) : null;
        return total + getPriceCent(item.quantity * parseFloat(product && !item.forceUseItemData ? product.weight : item.weight));
    }, 0));
}

export function getTotalQuantity(items, ignoreStatus = false) {
    return getPriceFromCent((items || []).filter(item => ignoreStatus || item.status === 'ordered').reduce((quantity, item) => quantity + getPriceCent((item && item.quantity * ((item.items || []).length ? item.items.reduce((packItemTotal, packItem) => packItemTotal + packItem.quantity, 0) : 1)) || 0), 0));
}

export function getSubtotal(items, products = null, includingTax = true, referral = null) {
    return getPriceFromCent(getPriceCent((items || []).filter(item => item.status === 'ordered').reduce((total, item) => {
        const priceKey = includingTax ? 'priceTTC' : 'priceHT';
        const product = false && products && products.length ? products.find(product => product._id === item.productId) : null;
        return total
			+ getProductPrice(item, item.quantity, includingTax, referral);
			// + (item.quantity * (product && !item.forceUseItemData ? product[priceKey] : item[priceKey]) || 0);
    }, 0)));
}

export function getTotalTax(items, products = null, taxRate = null, user = null, referral = null) {
    if(items && items.length && !getUserOption(user, 'exemptVAT')) {
        return formatPrice(
            items
                .filter(item => item.status === 'ordered')
                .map(item => ({
                    ...item,
                    ...((false && !item.forceUseItemData && products && products.length && products.find(product => product._id === item.productId)) || {}),
                }))
                .filter(item => parseFloat(item.taxRate) === parseFloat(taxRate) || taxRate === null)
                .reduce((subtotal, item) => {
                    const lineTotalTTC = getProductPrice(item, item.quantity, true, referral);
                    const lineTotalHT = lineTotalTTC / (1 + parseFloat(item.taxRate));
                    const lineTotalTax = lineTotalTTC - lineTotalHT;
                    return subtotal + lineTotalTax;
                }, 0),
        );
    }
    items && items.length && console.error('TAX set to 0!', items, products, taxRate, getUserOption(user, 'exemptVAT') && 'TVA exempted');
    return 0;
}

export function isConditionPercentValue(condition) {
    return ((condition || {}).value || '').indexOf('%') !== -1;
}

export function getConditionValue(condition) {
    let conditionValue = parseFloat(condition.value);
    if(isConditionPercentValue(condition)) {
        conditionValue = parseFloat(condition.value.replace('%', ''));
    }
    return conditionValue;
}

export function getConditionValueToCheck(condition, items, referral = null) {
    let valueToCheck = 0;

   if(condition.key === 'weight') { // check order total weight for all suborderitems
       valueToCheck = getTotalWeight(items);
   } else if(condition.key === 'subtotal') { // check order sub total for all suborderitems
       valueToCheck = getSubtotal(items, null, true, referral);
   } else if(condition.key === 'packCount') {
       valueToCheck = getPackages(items).length;
   } else {
       console.error('ConditionKeyMissing');
   }
   return valueToCheck;
}

export function isConditionChecked(condition, items, referral = null, exceptions = []) {
    // console.log('isConditionChecked ::: Start', condition, items, exceptions);

    if(exceptions && exceptions.includes(condition.key)) {
        // console.log('isConditionChecked ::: Allowed by exceptions');
        return true;
    }

    const conditionValue = getConditionValue(condition);
    const valueToCheck = getConditionValueToCheck(condition, items, referral);

    // console.log('isConditionChecked ::: Check', condition.key, `${valueToCheck}${isConditionPercentValue(condition) ? '%' : ''} ${condition.limit === 'min' ? '>' : '<'}${condition.tolerance === 'exclusive' ? '' : '='} ${conditionValue}${isConditionPercentValue(condition) ? '%' : ''}`);
    if(condition.limit === 'min') {
        if(condition.tolerance === 'exclusive') {
            return valueToCheck > conditionValue;
        }
        // inclusive
        return valueToCheck >= conditionValue;
    }
    // max
    if(condition.tolerance === 'exclusive') {
        return valueToCheck < conditionValue;
    }
    // inclusive
    return valueToCheck <= conditionValue;
}

export function getConditionCheckPercentage(condition, items, referral = null) {
    return (getConditionValueToCheck(condition, items, referral) * 100) / getConditionValue(condition);
}

export function formatAnalyticsItems(items) {
    const getCategories = item => {
        const categories = {};
        (item.categories || []).forEach((category, index) => {
            let categoryFieldName = 'item_category';
            if(index > 0) {
                categoryFieldName += index;
            }
            categories[categoryFieldName] = category;
        });
        return categories;
    };
    return (items || []).map(item => ({
        item_id: item.slug || slugify(item.name),
        item_name: item.name,
        price: formatPrice(parseFloat(item.priceTTC)),
        quantity: parseInt(item.quantity, 10),
        item_brand: 'MLSMS',
        ...getCategories(item),
    }));
}

export function getAvailableConditionKeys() {
    return ['weight', 'subtotal', 'packCount'];
}

// Actions
export function setCartItem(item) {
    return {
        type: SET_ITEM,
        item,
    };
}

export function setCartItemQuantity(itemId, quantity) {
    return {
        type: SET_ITEM_QUANTITY,
        itemId,
		quantity,
    };
}

export function removeItem(itemId) {
    return {
        type: REMOVE_ITEM,
        itemId,
    };
}

export function removeItems() {
    return {
        type: REMOVE_ITEMS,
    };
}

export function clearCart() {
    return {
        type: CLEAR_CART,
    };
}

export function setShippingAddress(address = {}) {
    return {
        type: SET_SHIPPING_ADDRESS,
        address,
    };
}

export function setShippingMethod(method = {}) {
    return {
        type: SET_SHIPPING_METHOD,
        method,
    };
}

export function setInvoiceAddress(address = {}) {
    return {
        type: SET_INVOICE_ADDRESS,
        address,
    };
}

export function setCartOption(key, value) {
    return {
        type: SET_CART_OPTION,
        key,
        value,
    };
}

export function setCartNote(note) {
    return {
        type: ADD_CART_NOTE,
        note,
    };
}
